/**
 * SHARED SOURCE OF TRUTH for the report-preferences form.
 *
 * This file is copied verbatim to the web client and the mobile app by
 * `yarn sync-preferences` (which `yarn migrate` runs automatically), exactly
 * like `db/schema.ts`. Do NOT edit the copies:
 *
 *   client/src/common/preferenceFields.ts        (generated)
 *   PoliceNarrativesMobile/common/preferenceFields.ts  (generated)
 *
 * Edit this file, run `yarn sync-preferences` from server/, and both platforms
 * pick up the change. Adding a preference here makes it appear on web AND iOS
 * with the same label, description, ordering, and options.
 *
 * Constraints that keep it portable — the copies must compile under CRA and
 * Expo, so this file must stay free of:
 *   - JSX / React imports (descriptions are plain strings, see below)
 *   - Node built-ins, server config, database access
 *   - anything outside `../db/schema`, which is itself synced to both
 *
 * DESCRIPTION MARKUP: descriptions are plain strings where `**text**` marks
 * emphasis. Each platform renders it natively (web: <b>, mobile: a bold
 * <Text>) via its own renderer, so no HTML crosses the boundary.
 */
import {
  CallSignFormatType,
  CallSignMentionType,
  NewReportPreferences,
  PreferenceDateFormatType,
  PreferencePerspectiveType,
  PreferenceStatuteStateType,
  PreferenceTimeFormatType,
  reportPreferences,
  SubjectReferenceStyleType,
} from "../db/schema";

export type PreferenceFieldType =
  | "toggle"
  | "dropdown"
  | "textarea"
  | "textinput"
  | "list";

export interface PreferenceField {
  label: string;
  /** Plain text; `**bold**` segments are rendered per-platform. */
  description: string;
  type: PreferenceFieldType;
  options?: { value: string; label: string }[];
  placeholder?: string;
  maxLength?: number;
  maxItems?: number;
}

/**
 * A controlling field plus dependent fields that are only shown while the
 * controller has a value. The group's key in the mapping is the controller's
 * preference key; children are keyed by their own preference keys.
 */
export interface PreferenceFieldGroup {
  type: "group";
  controller: PreferenceField;
  children: { [key: string]: PreferenceField };
}

export type PreferenceEntry = PreferenceField | PreferenceFieldGroup;

export interface PreferenceMapping {
  [key: string]: PreferenceEntry;
}

/** Default group visibility: truthy, treating whitespace-only strings as unset */
export const isFieldValueSet = (value: any): boolean =>
  typeof value === "string" ? value.trim().length > 0 : Boolean(value);

/**
 * Flattens groups into a key -> field map (controller + children) so lookups
 * by preference key (sanitization, labels, agency apply/push) work regardless
 * of nesting.
 */
export const flattenPreferenceFields = (
  mapping: PreferenceMapping
): { [key: string]: PreferenceField } => {
  const flat: { [key: string]: PreferenceField } = {};
  for (const [key, entry] of Object.entries(mapping)) {
    if (entry.type === "group") {
      flat[key] = entry.controller;
      Object.assign(flat, entry.children);
    } else {
      flat[key] = entry;
    }
  }
  return flat;
};

export interface BuildPreferenceFieldsOptions {
  /** Officer's (or agency's) display name, used in example sentences */
  entityName?: string;
  /** "user" for an officer, "agency" for department-wide defaults */
  entityType?: string;
  /** Current form values — drives the live call sign format examples */
  currentPrefs?: Partial<NewReportPreferences>;
  /** Officer's badge number, used in the call sign format examples */
  entityBadge?: string;
}

export const buildPreferenceFields = ({
  entityName,
  entityType,
  currentPrefs,
  entityBadge,
}: BuildPreferenceFieldsOptions = {}): PreferenceMapping => {
  // Example officer reference for the call sign format labels — built from
  // the officer's actual call sign, rank, name, and badge so the dropdown
  // shows exactly what the report will contain. Falls back to "Officer" when
  // no rank is set and "1-1" when no call sign is entered yet. The Include
  // Name / Include Badge Number toggles (both default on) decide which parts
  // appear.
  const exampleCallSign = (currentPrefs?.call_sign || "").trim() || "1-1";
  const exampleRank = (currentPrefs?.rank || "").trim() || "Officer";
  const exampleOfficerName =
    (entityType !== "agency" && entityName?.trim()) || "Smith";
  const exampleBadgeNumber = entityBadge?.trim() || "406";
  const includeExampleName = currentPrefs?.name ?? true;
  const includeExampleBadge = currentPrefs?.badge_number ?? true;
  const namePart = `${exampleRank} ${exampleOfficerName}`;
  const exampleOfficer =
    includeExampleName && includeExampleBadge
      ? `${namePart} #${exampleBadgeNumber}`
      : includeExampleName
      ? namePart
      : includeExampleBadge
      ? `#${exampleBadgeNumber}`
      : namePart;

  const callSignField: PreferenceField = {
    label: "Call Sign",
    description:
      `Enter your current call sign (e.g., **"1-1"** or **"5D6"**). Since call signs ` +
      `change by shift or assignment, you can adjust it for a specific report when uploading.`,
    type: "textinput",
    placeholder: "(ex: 1-1 or 5D6)",
  };

  const callSignFormatField: PreferenceField = {
    label: "Call Sign Format",
    description:
      "Choose how your call sign appears alongside your name in reports.",
    type: "dropdown",
    options: [
      {
        value: CallSignFormatType.enum.before_name,
        label: `${exampleCallSign} (${exampleOfficer})`,
      },
      {
        value: CallSignFormatType.enum.after_badge_dash,
        label: `${exampleOfficer} - ${exampleCallSign}`,
      },
      {
        value: CallSignFormatType.enum.after_name_parentheses,
        label: `${exampleOfficer} (${exampleCallSign})`,
      },
    ],
  };

  const callSignMentionField: PreferenceField = {
    label: "Call Sign Placement",
    description:
      "Include your call sign only the first time you are mentioned in the report, or every time.",
    type: "dropdown",
    options: [
      {
        value: CallSignMentionType.enum.first_mention,
        label: "First mention only",
      },
      {
        value: CallSignMentionType.enum.every_mention,
        label: "Every mention",
      },
    ],
  };

  return {
    [reportPreferences.badge_number.name]: {
      label: "Include Badge Number",
      description: `Uses badge # set on account. "I, ${
        entityName || "Officer Smith"
      } **(Badge #1118)**, responded to the area of …"`,
      type: "toggle",
    },
    [reportPreferences.topic_headings.name]: {
      label: "Include Topic Headings",
      description:
        "Organizes your report into bite-sized sections organized with topic headings to easily scan for information.",
      type: "toggle",
    },
    [reportPreferences.name.name]: {
      label: "Include Name",
      description: `Uses name set on account. "I, **${
        entityName || "Officer Smith"
      }**, responded to the area of …" vs "I responded to the area of …"`,
      type: "toggle",
    },
    [reportPreferences.capitalize_names.name]: {
      label: "Capitalize Names",
      description:
        "Automatically capitalizes first and last names for consistent formatting.",
      type: "toggle",
    },
    [reportPreferences.abbreviate_rank.name]: {
      label: "Abbreviate Rank",
      description:
        'Abbreviate rank when addressing other officers ("Ofc." vs "Officer")',
      type: "toggle",
    },
    [reportPreferences.capitalize_rank.name]: {
      label: "Capitalize Rank",
      description:
        'Capitalize rank when addressing other officers ("OFFICER" or "OFC" vs "officer" or "ofc")',
      type: "toggle",
    },
    [reportPreferences.rank.name]: {
      label: "Include Rank/Title",
      description:
        "Enter your official rank or title (e.g., Deputy, Detective, Lieutenant) to ensure " +
        'reports address you correctly. If not set, you will be referred to as **"Officer"**',
      type: "textinput",
      placeholder: "(ex: Deputy or Detective)",
    },
    // The Include Call Sign toggle reveals the call sign fields. Officers set
    // their own call sign; agency admins only configure the department style
    // (format + placement), so their group omits the call sign input.
    [reportPreferences.include_call_sign.name]: {
      type: "group" as const,
      controller: {
        label: "Include Call Sign",
        description:
          `Include your call sign when the report refers to you, e.g. ` +
          `**"${exampleCallSign} (${exampleOfficer})"**. Call signs change by shift or ` +
          `assignment, so you can adjust yours for a specific report when uploading.`,
        type: "toggle" as const,
      },
      children: {
        ...(entityType !== "agency"
          ? { [reportPreferences.call_sign.name]: callSignField }
          : {}),
        [reportPreferences.call_sign_format.name]: callSignFormatField,
        [reportPreferences.call_sign_mention.name]: callSignMentionField,
      },
    },
    [reportPreferences.statute_state.name]: {
      label: "Include Statutes",
      description:
        "Select your state to automatically include relevant statutes in your reports. " +
        '**"(HS § 11364 - Possession of Drug Paraphernalia)"**',
      type: "dropdown",
      options: Object.values(PreferenceStatuteStateType.Values).map(
        (state) => ({
          value: state,
          label: state === "" ? "No Statutes" : state,
        })
      ),
      placeholder: "Set State",
    },
    [reportPreferences.date_format.name]: {
      label: "Date Format",
      description: "Choose how dates should be formatted in the report.",
      type: "dropdown",
      options: [
        {
          value: PreferenceDateFormatType.enum["MMMM DD YYYY"],
          label: "January 31, 2024",
        },
        {
          value: PreferenceDateFormatType.enum["MM/DD/YYYY"],
          label: "1/31/2024",
        },
        {
          value: PreferenceDateFormatType.enum["DD/MM/YYYY"],
          label: "31/1/2024",
        },
        {
          value: PreferenceDateFormatType.enum["DD MMM YY"],
          label: "31 Jan 24",
        },
        {
          value: PreferenceDateFormatType.enum["DD MMMM YYYY"],
          label: "31 January 2024",
        },
      ],
    },
    [reportPreferences.time_format.name]: {
      label: "Time Format",
      description: "Choose between Military or 12-hour time format.",
      type: "dropdown",
      options: [
        {
          value: PreferenceTimeFormatType.enum.military_time,
          label: "Military (2100 hours)",
        },
        {
          value: PreferenceTimeFormatType.enum.standard_time,
          label: "12-hour (9:00 PM)",
        },
      ],
    },
    [reportPreferences.perspective.name]: {
      label: "Narrative Perspective",
      description: "Choose the point of view for the narrative.",
      type: "dropdown",
      options: [
        {
          value: PreferencePerspectiveType.enum.first_person,
          label: "First Person",
        },
        {
          value: PreferencePerspectiveType.enum.third_person,
          label: "Third Person",
        },
      ],
    },
    [reportPreferences.subject_reference_style.name]: {
      label: "Subject Reference Style",
      description:
        'Choose how to refer to subjects after first mention. ("Victim Doe" vs. "Victim" vs. "John Doe" vs. "Doe")',
      type: "dropdown",
      options: [
        {
          value:
            SubjectReferenceStyleType.enum
              .role_with_last_name_after_first_mention,
          label: "Role With Last Name After First Mention",
        },
        {
          value:
            SubjectReferenceStyleType.enum
              .role_with_first_name_after_first_mention,
          label: "Role With First Name After First Mention",
        },
        {
          value: SubjectReferenceStyleType.enum.role_after_first_mention,
          label: "Role After First Mention",
        },
        {
          value: SubjectReferenceStyleType.enum.last_name_after_first_mention,
          label: "Last Name After First Mention",
        },
        {
          value: SubjectReferenceStyleType.enum.first_name_after_first_mention,
          label: "First Name After First Mention",
        },
        {
          value: SubjectReferenceStyleType.enum.full_name_throughout,
          label: "Full Name Throughout",
        },
      ],
    },
    [reportPreferences.common_spellings.name]: {
      label: "Common Spellings",
      description:
        "Add unusual names, terms, or locations that appear in your reports. Useful for " +
        "officer names, local street names, and frequent locations. The system will " +
        "automatically use these correct spellings when dictated, saving you from having " +
        "to spell them out each time.",
      type: "list",
      placeholder:
        "(ex: Xiaoyang, Czajkowski, Scajaquada, Faneuil, Waxahachie, Sequim)",
    },
  };
};

/**
 * Splits a description into runs for rendering. `**bold**` becomes
 * `{ text, bold: true }`; everything else is plain. Each platform maps the
 * runs onto its own text primitives.
 */
export const parseDescription = (
  description: string
): { text: string; bold: boolean }[] => {
  const runs: { text: string; bold: boolean }[] = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(description)) !== null) {
    if (match.index > lastIndex) {
      runs.push({ text: description.slice(lastIndex, match.index), bold: false });
    }
    runs.push({ text: match[1], bold: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < description.length) {
    runs.push({ text: description.slice(lastIndex), bold: false });
  }
  return runs;
};
